import { useState, ChangeEvent, useEffect } from "react";
import { Dropdown, Button, FormControl } from "react-bootstrap";

import { BlueText } from "components/common";
import CustomDropdownToggle from "components/common/CustomDropdownToggle";
import CustomDropdownMenu from "components/common/CustomDropdownMenu";
import CustomFormCheck from "components/common/CustomFormCheck";

type SensorTypeDropdownProps = {
    sensorTypes: { id: string; name: string }[];
    selected: string[];
    onChange: (types: string[]) => void;
    className?: string;
};

export const SensorTypeDropdown = ({
    sensorTypes,
    selected,
    onChange,
    ...restProps
}: SensorTypeDropdownProps) => {
    const [searchText, setSearchText] = useState("");
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [filteredList, setFilteredList] = useState<any[]>([]);
    const [show, setShow] = useState(false);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setFilteredList(
            sensorTypes.filter(({ name }) =>
                name.toLowerCase().includes(e.target.value.toLowerCase())
            )
        );
    };

    const selectType = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked, value } = e.target;
        let types = selectedTypes.filter((t) => t !== value);

        if (checked) {
            types.push(value);
        }

        setSelectedTypes(types);
    };

    const clearFilter = () => {
        setSelectedTypes([]);
        onChange([]);
        setShow(false);
    };

    const applyFilter = () => {
        onChange([...selectedTypes]);
        setShow(false);
    };

    const handleOpenMenu = (isOpen: boolean) => {
        setShow(isOpen);
        setSelectedTypes(selected);
        setSearchText("");
        setFilteredList(sensorTypes);
    };

    useEffect(() => {
        setFilteredList(sensorTypes);
    }, [sensorTypes]);

    useEffect(() => {
        setSelectedTypes(selected);
    }, [selected]);

    return (
        <Dropdown show={show} onToggle={handleOpenMenu} {...restProps}>
            <CustomDropdownToggle>
                <i className="material-icons-outlined mr-2">filter_alt</i>
                <span title="Filter">Filter</span>
                {selectedTypes.length > 0 && <i className="dot ml-2" />}
                <i className="material-icons down ml-2">
                    keyboard_arrow_down
                </i>
            </CustomDropdownToggle>

            <CustomDropdownMenu className="px-3 py-3">
                <h6 className="">Filter</h6>
                <FormControl
                    className="search mt-3 mb-2"
                    placeholder="Search for sensor types"
                    aria-label="search-dashboard"
                    onChange={handleSearch}
                    value={searchText}
                />
                {selectedTypes.length > 0 && (
                    <div>
                        <BlueText className="d-block mb-2">
                            Selected Types
                        </BlueText>
                        {selectedTypes.map((type) => (
                            <CustomFormCheck
                                key={type}
                                className="mb-2"
                                name="selected-sensor"
                                value={type}
                                checked={true}
                                custom
                                onChange={selectType}
                                id={type}
                                label={<span className="ml-2">{type}</span>}
                            />
                        ))}
                    </div>
                )}
                <BlueText className="d-block mb-2">Sensor Types</BlueText>
                <div className="check-list">
                    {filteredList
                        .filter((t) => !selectedTypes.includes(t.id))
                        .map((option: any) => (
                            <CustomFormCheck
                                key={option.id}
                                className="mb-2"
                                name="selected-sensor"
                                value={option.id}
                                checked={selectedTypes.includes(option.id)}
                                custom
                                onChange={selectType}
                                id={option.id}
                                label={
                                    <span className="ml-2">
                                        {option.name}
                                    </span>
                                }
                            />
                        ))}
                </div>
                <div className="d-flex justify-content-between mt-3">
                    <Button
                        variant="dark"
                        onClick={clearFilter}
                        size="sm"
                        className="px-md-3"
                    >
                        CLEAR
                    </Button>
                    <Button
                        className="px-md-3"
                        variant="primary"
                        onClick={applyFilter}
                        size="sm"
                    >
                        APPLY
                    </Button>
                </div>
            </CustomDropdownMenu>
        </Dropdown>
    );
};
